import { equal, handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  SecurityCultureSurvey,
  SurveyCategory,
  SurveyStatus,
} from '@/client/security-culture/types';
import { EmptyProductCard } from '@/components/dashboards/admin';
import {
  SCScoreBars,
  SecurityCultureScore,
} from '@/components/security-culture';
import {
  useSurveyReport,
  useSurveys,
} from '@/hooks/query/security-culture.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { FlexContainer } from '@/ui';
import { ProductType } from '@/utils/helpers';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledScoreContainer = styled(FlexContainer)`
  @media screen and (max-width: 1450px) {
    flex-wrap: wrap;

    > div {
      flex: none;
    }
  }
`;

type SCMainDashboardV2Props = {
  setSurveyId?: (surveyId: string) => void;
};

export const SCMainDashboardV2 = ({ setSurveyId }: SCMainDashboardV2Props) => {
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const [survey, setSurvey] = useState<SecurityCultureSurvey>();
  const [categories, setCategories] = useState<SurveyCategory[]>();
  const [areCategoriesLoading, setAreCategoriesLoading] = useState(true);
  const [isEmptySCDashboard, setIsEmptySCDashboard] = useState(false);

  const { surveys, isLoading, refetch } = useSurveys(
    {
      sort: ['date,desc'],
      filters: [
        equal('status', SurveyStatus.PUBLISHED),
        equal('isDemo', false),
      ],
      take: 1,
    },
    account?.id,
  );

  const fetchCategories = async (survey?: SecurityCultureSurvey) => {
    if (!survey) {
      setCategories(undefined);
      return;
    }
    try {
      setAreCategoriesLoading(true);
      const response = await client.securityCulture.getSurveyCategories(
        account?.id,
        survey?.id,
      );

      setCategories(response.result);
    } catch (e) {
      handleAxiosError(e, toast);
    } finally {
      setAreCategoriesLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading) return;

    setIsEmptySCDashboard(surveys?.count === 0);

    if (!surveys?.count) {
      setSurvey(undefined);
      setCategories(undefined);
      setAreCategoriesLoading(false);
      return;
    }

    setSurvey(surveys.result[0]);
    fetchCategories(surveys.result[0]);
    setSurveyId?.(surveys.result[0].id as string);
  }, [surveys, isLoading]);

  useEffect(() => {
    refetch();
  }, [account]);

  const { surveyReports, isLoading: areSurveyReportsLoading } = useSurveyReport(
    {
      surveyId: survey?.id ? [survey.id] : [],
      enabled: !!survey?.id,
    },
    account?.id,
  );

  const isScoreWithheld =
    !!survey?.id &&
    !areSurveyReportsLoading &&
    !!surveyReports &&
    surveyReports.surveyScore === null;

  if (isScoreWithheld) {
    return (
      <EmptyProductCard
        type={ProductType.SECURITY_CULTURE_DASHBOARD}
        id="sc-main-dashboard-anonymity"
      />
    );
  }

  return (
    <>
      <StyledScoreContainer
        id="sc-score-container-main-dashboard"
        style={{ marginTop: 'var(--big-padding)' }}
        justify="space-between"
        align="stretch"
        gap={14}
      >
        <SecurityCultureScore
          survey={isEmptySCDashboard ? undefined : surveys?.result[0]}
          isLoading={isLoading}
          isMainDashboard
          isEmpty={isEmptySCDashboard}
          hasPreviousScore={surveys && surveys.count > 1}
          isV2
        />
        <SCScoreBars
          categories={categories}
          surveyId={survey?.id}
          isLoading={isLoading || areCategoriesLoading}
          isMainDashboard
          isEmpty={isEmptySCDashboard}
          isV2
        />
      </StyledScoreContainer>
    </>
  );
};
